import { safeLocalStorage } from '@client/utils/storage/safe-storage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
    /** User's theme preference */
    theme: ThemeMode;
    /** Actual theme being applied (resolved from system preference if needed) */
    resolvedTheme: ResolvedTheme;
    /**
     * When set, wins over the preference and system changes. Public pages force
     * 'light' through this so the root antd ConfigProvider follows along — portaled
     * surfaces (modal, message, notification) would otherwise render in the
     * visitor's dark preference on a light public form.
     */
    forcedTheme: ResolvedTheme | null;
    /** Set the theme preference */
    setTheme: (theme: ThemeMode) => void;
    /** Initialize theme on app load (call once) */
    initializeTheme: () => void;
    /** Force a theme (public pages) or clear the force with null */
    setForcedTheme: (forcedTheme: ResolvedTheme | null) => void;
}

/**
 * Get the system's preferred colour scheme
 */
function getSystemTheme(): ResolvedTheme {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve the actual theme to apply based on user preference
 */
function resolveTheme(theme: ThemeMode): ResolvedTheme {
    if (theme === 'system') {
        return getSystemTheme();
    }
    return theme;
}

/**
 * Public pages always render light, regardless of the visitor's stored or system
 * preference. Checked at store creation (and in `initializeThemeEarly`) so a direct
 * load of a public URL never paints a dark frame before the route mounts and forces
 * the theme itself.
 */
function isPublicPath(pathname: string): boolean {
    return pathname === '/public' || pathname.startsWith('/public/');
}

function initialForcedTheme(): ResolvedTheme | null {
    if (typeof window === 'undefined') return null;
    return isPublicPath(window.location.pathname) ? 'light' : null;
}

/**
 * Apply the theme to the document
 */
function applyTheme(resolvedTheme: ResolvedTheme): void {
    if (typeof document === 'undefined') return;

    // Set data-theme attribute on html element for CSS
    document.documentElement.setAttribute('data-theme', resolvedTheme);

    // Update color-scheme for native elements (scrollbars, inputs, etc.)
    document.documentElement.style.colorScheme = resolvedTheme;
}

export const useThemeStore = create<ThemeState>()(
    persist(
        (set, get) => ({
            theme: 'system',
            resolvedTheme: 'light', // Will be updated on initialization
            forcedTheme: initialForcedTheme(),

            setTheme: (theme: ThemeMode) => {
                const resolved = get().forcedTheme ?? resolveTheme(theme);
                applyTheme(resolved);
                set({ theme, resolvedTheme: resolved });
            },

            initializeTheme: () => {
                const { theme, forcedTheme } = get();
                const resolved = forcedTheme ?? resolveTheme(theme);
                applyTheme(resolved);
                set({ resolvedTheme: resolved });
            },

            setForcedTheme: (forcedTheme: ResolvedTheme | null) => {
                const resolved = forcedTheme ?? resolveTheme(get().theme);
                applyTheme(resolved);
                set({ forcedTheme, resolvedTheme: resolved });
            },
        }),
        {
            name: 'curium-theme',
            storage: createJSONStorage(() => safeLocalStorage),
            partialize: (state) => ({
                theme: state.theme,
            }),
            onRehydrateStorage: () => (state) => {
                // After rehydration, resolve and apply the theme
                if (state) {
                    const resolved = state.forcedTheme ?? resolveTheme(state.theme);
                    applyTheme(resolved);
                    state.resolvedTheme = resolved;
                }
            },
        },
    ),
);

// Listen for system theme changes
if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
        const state = useThemeStore.getState();
        if (state.theme === 'system' && !state.forcedTheme) {
            const resolved = e.matches ? 'dark' : 'light';
            applyTheme(resolved);
            useThemeStore.setState({ resolvedTheme: resolved });
        }
    };

    // Use addEventListener for modern browsers
    mediaQuery.addEventListener('change', handleSystemThemeChange);
}

/**
 * Initialize theme before React renders to prevent flash of wrong theme.
 * Call this in the HTML head or at the very top of main.tsx.
 */
export function initializeThemeEarly(): void {
    if (typeof window === 'undefined') return;

    if (isPublicPath(window.location.pathname)) {
        applyTheme('light');
        return;
    }

    try {
        const stored = localStorage.getItem('curium-theme');
        if (stored) {
            const { state } = JSON.parse(stored);
            const theme: ThemeMode = state?.theme || 'system';
            const resolved = resolveTheme(theme);
            applyTheme(resolved);
        } else {
            // No stored preference, use system default
            const resolved = getSystemTheme();
            applyTheme(resolved);
        }
    } catch {
        // Fallback to light theme on error
        applyTheme('light');
    }
}
